<template>
  <div class="container-fluid mt-4 nutricion-page">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div class="min-w-0">
        <h2 class="mb-0"><i class="bi bi-egg-fried me-2"></i>Nutrición</h2>
        <small class="text-muted">Menú de los próximos días generado por el agente</small>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap nutricion-acciones">
        <router-link
          class="btn btn-sm"
          :class="desdeLista ? 'btn-success' : 'btn-outline-success'"
          to="/lista-compra"
        >
          <i class="bi" :class="desdeLista ? 'bi-arrow-left' : 'bi-cart3'"></i>
          {{ desdeLista ? 'Volver a la lista' : 'Lista de la compra' }}
        </router-link>
        <a
          :href="metamorfosisUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-outline-success btn-sm"
        >
          <i class="bi bi-globe me-1"></i>Web metamorfosis
        </a>
        <button class="btn btn-outline-primary btn-sm" @click="actualizar" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          {{ cargando ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
    </div>

    <ul class="nav nav-tabs mb-4 flex-wrap nutricion-tabs" role="tablist">
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: tabActivo === 'proximos' }"
          @click="tabActivo = 'proximos'"
        >
          <i class="bi bi-calendar3 me-1"></i>Próximos días
        </button>
      </li>
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: tabActivo === 'agente' }"
          @click="abrirAgente"
        >
          <i class="bi bi-robot me-1"></i>Agente
        </button>
      </li>
      <li v-if="isAdmin" class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: tabActivo === 'config' }"
          @click="tabActivo = 'config'; cargarConfig()"
        >
          <i class="bi bi-gear me-1"></i>Configuración
        </button>
      </li>
    </ul>

    <div v-show="tabActivo === 'proximos'">
      <NutricionProximosDias ref="proximosRef" :is-admin="isAdmin" />
    </div>

    <div v-if="tabActivo === 'agente' && isAdmin">
      <div class="alert alert-light border small mb-3">
        El agente usa la rotación de esta semana y la próxima como base.
        Tras generar y aceptar, el menú pasa a <strong>Próximos días</strong>,
        donde se puede editar.
      </div>

      <div v-if="cargando && !datos" class="text-center py-4">
        <div class="spinner-border text-primary"></div>
        <p class="mt-2 text-muted">Cargando rotación desde Trello…</p>
      </div>

      <div v-else class="row g-3 mb-4">
        <div class="col-lg-6">
          <NutricionPlanCard
            variant="actual"
            etiqueta="Esta semana"
            :semana="datos?.semanaActual"
            :rango="formatRangoSemana(datos?.fechaInicioSemana)"
            :tiene-override="!!datos?.tieneOverride"
            :is-admin="isAdmin"
            @cambiar="abrirOverride"
            @restaurar="quitarOverride"
          />
        </div>
        <div class="col-lg-6">
          <NutricionPlanCard
            variant="proxima"
            etiqueta="Próxima semana"
            :semana="datos?.semanaProxima"
          />
        </div>
      </div>

      <div v-if="mostrarOverride && isAdmin" class="card mt-0 mb-4 shadow-sm border-warning">
        <div class="card-header bg-warning text-dark d-flex align-items-center justify-content-between">
          <span><i class="bi bi-shuffle me-2"></i>Cambiar plan de esta semana</span>
          <button class="btn-close" @click="mostrarOverride = false"></button>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            Selecciona el plan que quieres mostrar esta semana en lugar del de la rotación.
            El cambio es permanente hasta que lo restaures o cambies la configuración.
          </p>
          <div v-if="cargandoPlanes" class="text-center py-2">
            <div class="spinner-border spinner-border-sm text-primary"></div>
          </div>
          <div v-else class="d-flex gap-2 flex-wrap">
            <select class="form-select" v-model="overrideSeleccionado" style="max-width:300px;">
              <option value="">-- Selecciona un plan --</option>
              <option v-for="plan in planesDisponibles" :key="plan.id" :value="plan.id">
                {{ plan.nombre }}
              </option>
            </select>
            <button
              class="btn btn-warning"
              :disabled="!overrideSeleccionado || guardandoOverride"
              @click="guardarOverride"
            >
              <span v-if="guardandoOverride" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Aplicar
            </button>
            <button class="btn btn-outline-secondary" @click="mostrarOverride = false">
              Cancelar
            </button>
          </div>
        </div>
      </div>

      <NutricionAgente @aceptado="onMenuAceptado" />
    </div>

    <div v-if="tabActivo === 'config' && isAdmin">
      <div class="card shadow-sm">
        <div class="card-header">
          <h5 class="mb-0"><i class="bi bi-sliders me-2"></i>Configurar rotación</h5>
        </div>
        <div class="card-body">
          <div v-if="cargandoConfig" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
          </div>
          <form v-else @submit.prevent="guardarConfig">
            <div class="alert alert-light border small mb-4">
              <i class="bi bi-info-circle me-1"></i>
              Los menús salen de las descripciones de Trello. Además de
              <strong>COMIDAS HABITUALES</strong>, la app lee
              <strong>CENAS HABITUALES</strong>,
              <strong>COMIDAS FIN DE SEMANA</strong> y
              <strong>CENAS FIN DE SEMANA</strong>.
            </div>
            <div class="mb-4">
              <label class="form-label fw-semibold">
                <i class="bi bi-calendar-event me-1"></i>Fecha de inicio de la rotación
              </label>
              <input
                type="date"
                class="form-control"
                v-model="configForm.fecha_inicio"
                required
              />
              <div class="form-text">
                La semana cuya fecha de inicio corresponde a esta fecha mostrará el primer plan de la secuencia.
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-semibold">
                <i class="bi bi-list-ol me-1"></i>Secuencia de planes
              </label>
              <div v-if="cargandoPlanes" class="text-muted small">Cargando planes...</div>
              <div v-else>
                <p class="text-muted small mb-2">
                  Selecciona los planes en el orden de rotación. Un mismo plan puede aparecer varias veces.
                </p>
                <div class="mb-3">
                  <div
                    v-for="(planId, idx) in configForm.secuencia"
                    :key="idx"
                    class="d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light flex-wrap"
                  >
                    <span class="badge bg-primary">{{ idx + 1 }}</span>
                    <span class="flex-grow-1">{{ nombrePlan(planId) }}</span>
                    <a
                      v-if="planPdfUrl(planId)"
                      :href="planPdfUrl(planId)"
                      target="_blank"
                      class="btn btn-outline-primary btn-sm"
                      title="Plan semana"
                    ><i class="bi bi-file-earmark-pdf"></i></a>
                    <a
                      v-if="planRecetarioUrl(planId)"
                      :href="planRecetarioUrl(planId)"
                      target="_blank"
                      class="btn btn-outline-secondary btn-sm"
                      title="Recetario"
                    ><i class="bi bi-book"></i></a>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="idx === 0"
                      @click="moverArriba(idx)"
                      title="Subir"
                    ><i class="bi bi-arrow-up"></i></button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="idx === configForm.secuencia.length - 1"
                      @click="moverAbajo(idx)"
                      title="Bajar"
                    ><i class="bi bi-arrow-down"></i></button>
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      @click="eliminarDeLista(idx)"
                      title="Eliminar"
                    ><i class="bi bi-x"></i></button>
                  </div>
                </div>

                <div class="d-flex gap-2 align-items-center">
                  <select class="form-select form-select-sm" v-model="planSeleccionado">
                    <option value="">-- Añadir plan --</option>
                    <option
                      v-for="plan in planesDisponibles"
                      :key="plan.id"
                      :value="plan.id"
                    >{{ plan.nombre }}</option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="!planSeleccionado"
                    @click="agregarALista"
                  >
                    <i class="bi bi-plus"></i> Añadir
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-semibold">
                <i class="bi bi-cup-hot me-1"></i>Desayuno de cada día
              </label>
              <div v-for="(dia, idx) in diasSemana" :key="dia" class="input-group input-group-sm mb-1">
                <span class="input-group-text" style="min-width: 6.5rem">{{ dia }}</span>
                <input
                  type="text"
                  class="form-control"
                  maxlength="200"
                  v-model="configForm.desayunos[idx]"
                  :aria-label="`Desayuno del ${dia}`"
                />
              </div>
              <div class="form-text">
                Se copia al menú de cada día (no lo genera la IA). Si lo dejas vacío se usa el valor por defecto.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Aviso de platos congelados</label>
              <div class="form-check form-switch">
                <input
                  id="congelados-aviso-activo"
                  v-model="configForm.congeladosAviso.activo"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                />
                <label class="form-check-label" for="congelados-aviso-activo">
                  Avisarme el día antes de un plato marcado como congelado
                </label>
              </div>
              <div class="input-group input-group-sm mt-2" style="max-width: 16rem">
                <span class="input-group-text">Hora del aviso</span>
                <select
                  v-model.number="configForm.congeladosAviso.hora"
                  class="form-select"
                  :disabled="!configForm.congeladosAviso.activo"
                  aria-label="Hora del aviso de congelados"
                >
                  <option v-for="h in 24" :key="h" :value="h - 1">{{ String(h - 1).padStart(2, '0') }}:00</option>
                </select>
              </div>
              <div class="form-text">
                Marca los platos congelados con el icono de copo al editar Próximos días. Por defecto avisa a las 21:00 del día anterior.
              </div>
            </div>

            <div class="d-flex gap-2">
              <button type="submit" class="btn btn-primary" :disabled="guardandoConfig">
                <span v-if="guardandoConfig" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="bi bi-check-lg me-1"></i>
                {{ guardandoConfig ? 'Guardando...' : 'Guardar configuración' }}
              </button>
            </div>

            <div v-if="mensajeConfig" class="alert mt-3" :class="errorConfig ? 'alert-danger' : 'alert-success'">
              {{ mensajeConfig }}
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import axios from '@/config/axios';
import NutricionPlanCard from '@/components/NutricionPlanCard.vue';
import NutricionAgente from '@/components/NutricionAgente.vue';
import NutricionProximosDias from '@/components/NutricionProximosDias.vue';

export default {
  name: 'Nutricion',
  components: { NutricionPlanCard, NutricionAgente, NutricionProximosDias },
  setup() {
    const store = useStore();
    const route = useRoute();
    const isAdmin = computed(() => store.getters['auth/isAdmin']);
    const desdeLista = computed(() => route.query.from === 'lista');

    const tabActivo = ref('proximos');
    const cargando = ref(false);
    const error = ref(null);
    const datos = ref(null);
    const proximosRef = ref(null);

    const mostrarOverride = ref(false);
    const overrideSeleccionado = ref('');
    const guardandoOverride = ref(false);

    const cargandoConfig = ref(false);
    const cargandoPlanes = ref(false);
    const guardandoConfig = ref(false);
    const mensajeConfig = ref('');
    const errorConfig = ref(false);
    const planesDisponibles = ref([]);
    const planSeleccionado = ref('');
    const configForm = ref({
      fecha_inicio: '',
      secuencia: [],
      desayunos: ['', '', '', '', '', '', ''],
      congeladosAviso: { activo: true, hora: 21 }
    });
    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const metamorfosisUrl = '/metamorfosis_3.html#';

    const formatFecha = (fechaStr) => {
      if (!fechaStr) return '';
      const d = new Date(`${fechaStr}T00:00:00`);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const formatRangoSemana = (fechaInicioStr) => {
      if (!fechaInicioStr) return '';
      const inicio = new Date(`${fechaInicioStr}T00:00:00`);
      const fin = new Date(inicio);
      fin.setDate(fin.getDate() + 6);
      const inicioTxt = formatFecha(fechaInicioStr);
      const finTxt = fin.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
      return `Desde el ${inicioTxt} hasta el domingo ${finTxt}`;
    };

    const cargarDatos = async () => {
      cargando.value = true;
      error.value = null;
      try {
        const res = await axios.get('/api/nutricion/semana-actual');
        datos.value = res.data;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar la rotación semanal';
      } finally {
        cargando.value = false;
      }
    };

    const actualizar = async () => {
      await Promise.all([
        proximosRef.value?.cargar?.(),
        isAdmin.value ? cargarDatos() : Promise.resolve(),
      ]);
    };

    const abrirAgente = () => {
      tabActivo.value = 'agente';
      if (!datos.value && !cargando.value) cargarDatos();
    };

    const cargarPlanes = async () => {
      if (planesDisponibles.value.length > 0) return;
      cargandoPlanes.value = true;
      try {
        const res = await axios.get('/api/nutricion/planes');
        planesDisponibles.value = res.data;
      } catch (e) {
        console.error('Error al cargar planes:', e);
      } finally {
        cargandoPlanes.value = false;
      }
    };

    const cargarConfig = async () => {
      await cargarPlanes();
      cargandoConfig.value = true;
      try {
        const res = await axios.get('/api/nutricion/config');
        configForm.value.fecha_inicio = res.data.fecha_inicio || '';
        configForm.value.secuencia = res.data.secuencia || [];
        const des = await axios.get('/api/nutricion/desayunos');
        configForm.value.desayunos = des.data.desayunos || configForm.value.desayunos;
        const cong = await axios.get('/api/nutricion/congelados-aviso');
        configForm.value.congeladosAviso = { activo: !!cong.data.activo, hora: cong.data.hora };
      } catch (e) {
        console.error('Error al cargar config:', e);
      } finally {
        cargandoConfig.value = false;
      }
    };

    const guardarConfig = async () => {
      guardandoConfig.value = true;
      mensajeConfig.value = '';
      errorConfig.value = false;
      try {
        const { desayunos, congeladosAviso, ...config } = configForm.value;
        await axios.put('/api/nutricion/config', config);
        const res = await axios.put('/api/nutricion/desayunos', { desayunos });
        configForm.value.desayunos = res.data.desayunos;
        await axios.put('/api/nutricion/congelados-aviso', congeladosAviso);
        mensajeConfig.value = 'Configuración guardada correctamente.';
        await cargarDatos();
      } catch (e) {
        errorConfig.value = true;
        mensajeConfig.value = e.response?.data?.error || 'Error al guardar la configuración';
      } finally {
        guardandoConfig.value = false;
      }
    };

    const abrirOverride = async () => {
      await cargarPlanes();
      overrideSeleccionado.value = datos.value?.semanaActual?.cardId || '';
      mostrarOverride.value = true;
    };

    const guardarOverride = async () => {
      guardandoOverride.value = true;
      try {
        await axios.put('/api/nutricion/override', { override_card_id: overrideSeleccionado.value });
        mostrarOverride.value = false;
        await cargarDatos();
      } catch (e) {
        console.error('Error al guardar override:', e);
      } finally {
        guardandoOverride.value = false;
      }
    };

    const quitarOverride = async () => {
      try {
        await axios.put('/api/nutricion/override', { override_card_id: null });
        await cargarDatos();
      } catch (e) {
        console.error('Error al quitar override:', e);
      }
    };

    const nombrePlan = (planId) => {
      const plan = planesDisponibles.value.find((p) => p.id === planId);
      return plan ? plan.nombre : planId;
    };

    const planPdfUrl = (planId) => {
      const plan = planesDisponibles.value.find((p) => p.id === planId);
      return plan ? plan.pdfUrl : null;
    };

    const planRecetarioUrl = (planId) => {
      const plan = planesDisponibles.value.find((p) => p.id === planId);
      return plan ? plan.recetarioUrl : null;
    };

    const moverArriba = (idx) => {
      if (idx === 0) return;
      const arr = [...configForm.value.secuencia];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      configForm.value.secuencia = arr;
    };

    const moverAbajo = (idx) => {
      const arr = [...configForm.value.secuencia];
      if (idx === arr.length - 1) return;
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      configForm.value.secuencia = arr;
    };

    const eliminarDeLista = (idx) => {
      configForm.value.secuencia.splice(idx, 1);
    };

    const agregarALista = () => {
      if (planSeleccionado.value) {
        configForm.value.secuencia.push(planSeleccionado.value);
        planSeleccionado.value = '';
      }
    };

    const onMenuAceptado = async () => {
      tabActivo.value = 'proximos';
      await nextTick();
      await proximosRef.value?.cargar?.();
    };

    onMounted(() => {
      if (isAdmin.value) cargarDatos();
    });

    return {
      isAdmin,
      desdeLista,
      tabActivo,
      cargando,
      error,
      datos,
      proximosRef,
      mostrarOverride,
      overrideSeleccionado,
      guardandoOverride,
      cargandoConfig,
      cargandoPlanes,
      guardandoConfig,
      mensajeConfig,
      errorConfig,
      planesDisponibles,
      planSeleccionado,
      configForm,
      diasSemana,
      metamorfosisUrl,
      formatRangoSemana,
      actualizar,
      abrirAgente,
      cargarConfig,
      guardarConfig,
      abrirOverride,
      guardarOverride,
      quitarOverride,
      nombrePlan,
      planPdfUrl,
      planRecetarioUrl,
      moverArriba,
      moverAbajo,
      eliminarDeLista,
      agregarALista,
      onMenuAceptado,
    };
  }
};
</script>

<style scoped>
.nutricion-tabs .nav-link {
  white-space: nowrap;
}
@media (max-width: 575.98px) {
  .nutricion-acciones {
    width: 100%;
  }
  .nutricion-acciones .btn {
    flex: 1 1 auto;
  }
}
</style>
